import type { AuthedRequester } from "@/lib/brokerAuth";
import type { BondSubmissionListItem } from "@/lib/brokerBonds";
import type { BondPhaseKey } from "@/lib/bondStateModel";
import { formatBondDate } from "@/lib/bondValueDisplay";
import { listBondHistory } from "@/lib/bondHistory";
import { MAX_PAGE_SIZE } from "@/base/list/defineListView";

// BRK-S403 — the Surety program's ACTIVE BONDS: the insured's submissions
// whose job is in a phase that holds program capacity (proposal §10.3: `bid`,
// `award`, `performance_period`; `maintenance` and `closed` release it).
//
// SELECTED AND TOTALLED BY PMS (PMS-S859, BRK-S511). The list's `?phase=`
// filter answers exactly those rows and its `totals` the count and contract
// price over them, so the SPA reads one page and sums nothing. The client-side
// walk over every page, and the status fallback for a PMS that predated the
// served phase, are gone: nothing is in production, so there is no such PMS.

/** The phases whose bonds hold program capacity. */
export const ACTIVE_BOND_PHASES: readonly BondPhaseKey[] = [
  "bid",
  "award",
  "performance_period",
];

export interface ActiveBondsTotals {
  count: number;
  /** Null when no row serves a contract price: a dash, never $0. */
  contractPrice: number | null;
}

export interface ActiveBonds {
  /** The rows PMS served, in its order — the first page, up to the largest
   *  page the list allows. */
  rows: BondSubmissionListItem[];
  /** PMS's aggregates over EVERY active bond, not just the rows served. */
  totals: ActiveBondsTotals;
}

/** GET /api/broker/bond/submissions?accountId=&phase=bid,award,performance_period */
export async function readActiveBonds(
  request: AuthedRequester,
  accountId: string,
): Promise<ActiveBonds> {
  const page = await listBondHistory(request, {
    accountId,
    phase: ACTIVE_BOND_PHASES,
    limit: MAX_PAGE_SIZE,
    offset: 0,
  });
  return {
    rows: page.items,
    totals: {
      count: page.totals.count,
      contractPrice: page.totals.contractPrice,
    },
  };
}

/** A served figure, or null. A formatted string from the wire is not one
 *  (D-10): it reads as absent rather than as a number somebody parsed. */
export function activeBondFigure(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T|$)/;

/** A project date for display, `m/d/yyyy` (D-10), or null for an absence or
 *  anything that is not an ISO date — a formatted value is not accepted from
 *  the wire. */
export function activeBondDate(value: unknown): string | null {
  return typeof value === "string" && ISO_DATE.test(value)
    ? formatBondDate(value)
    : null;
}
